import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { COMMON_ROUTE } from "../../constants/routes";
import history from "../../routers/history";
import {
  trackEvent,
  trackEventWithTimeout,
} from "../../service/event-service";

// The four-colour Google "G", inlined so the page makes no external request.
function GoogleMark() {
  return (
    <svg
      className="google-mark"
      viewBox="0 0 48 48"
      width="40"
      height="40"
      aria-hidden="true"
      focusable="false"
    >
      <path
        fill="#EA4335"
        d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
      />
      <path
        fill="#4285F4"
        d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
      />
      <path
        fill="#FBBC05"
        d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
      />
      <path
        fill="#34A853"
        d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
      />
    </svg>
  );
}

// The four-colour Google "G" as an SVG favicon, so the browser tab matches the
// page. Inlined as a data URI — no external request.
const GOOGLE_FAVICON =
  "data:image/svg+xml," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 48 48">' +
      '<path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>' +
      '<path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>' +
      '<path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>' +
      '<path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>' +
      "</svg>"
  );

export default function AccountSignin() {
  const location = useLocation();
  const [step, setStep] = useState("identifier");
  const [advancing, setAdvancing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    window.document.title = "Sign in - Google Accounts";

    // Swap the tab favicon to Google's while this page is shown, then restore
    // the site icon on the way out so the reveal and other pages are unchanged.
    const iconLink = document.querySelector("link[rel~='icon']");
    const previousHref = iconLink ? iconLink.getAttribute("href") : null;
    if (iconLink) iconLink.setAttribute("href", GOOGLE_FAVICON);

    // The mockup landing loaded (the "Check activity" link was opened).
    // Scanners can trigger this; clicks/submits are the stronger signals.
    trackEvent("landing_viewed");

    return () => {
      if (iconLink && previousHref != null) {
        iconLink.setAttribute("href", previousHref);
      }
    };
  }, []);

  // Advancing from the account step. The typed identifier is intentionally
  // never read: we only record that the person moved past the first screen.
  const submitIdentifier = async (event) => {
    event.preventDefault();

    if (advancing) return;

    setAdvancing(true);
    event.currentTarget.reset();

    try {
      await trackEventWithTimeout("cta_clicked");
    } catch (error) {
      // Recording is best-effort; never block the simulation on it.
    }

    setAdvancing(false);
    setShowPassword(false);
    setStep("password");
  };

  const submitPassword = async (event) => {
    event.preventDefault();

    if (submitting) return;

    setSubmitting(true);

    // The inputs deliberately have no names and their values are never read.
    // Clear them immediately while recording only that the form was submitted.
    const form = event.currentTarget;
    form.reset();

    let result;

    try {
      result = await trackEventWithTimeout("form_submitted");
    } catch (error) {
      result = { recorded: false, reason: "write-failed" };
    }

    const trackingStatus = result.recorded
      ? "recorded"
      : result.reason === "timeout"
      ? "pending"
      : "failed";
    const params = new URLSearchParams(location.search);
    params.set("s", "google-signin");

    history.push({
      pathname: COMMON_ROUTE.training.path,
      search: `?${params.toString()}`,
      state: { trackingStatus },
    });
  };

  return (
    <div className="google-signin-page">
      <main className="google-signin-shell">
        <section
          className="google-card"
          aria-labelledby="google-signin-title"
        >
          <div className="google-card-body">
            <div className="google-card-head">
              <GoogleMark />
              {step === "identifier" ? (
                <>
                  <h1 id="google-signin-title">Sign in</h1>
                  <p>Use your Google Account</p>
                </>
              ) : (
                <>
                  <h1 id="google-signin-title">Welcome</h1>
                  <div className="google-account-chip">
                    <span className="google-avatar" aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="20" height="20">
                        <path
                          fill="currentColor"
                          d="M12 12a5 5 0 1 0 0-10 5 5 0 0 0 0 10zm0 2c-4.42 0-8 2.24-8 5v1h16v-1c0-2.76-3.58-5-8-5z"
                        />
                      </svg>
                    </span>
                    <span>Your Google Account</span>
                  </div>
                </>
              )}
            </div>

            {step === "identifier" ? (
              <form
                className="google-form"
                autoComplete="off"
                data-form-type="other"
                onSubmit={submitIdentifier}
                aria-busy={advancing}
              >
                <div className="google-field">
                  <input
                    id="identifier"
                    type="email"
                    inputMode="email"
                    placeholder=" "
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-lpignore="true"
                    required
                  />
                  <label htmlFor="identifier">Email or phone</label>
                </div>

                <span className="google-inline-link">Forgot email?</span>

                <p className="google-guest-note">
                  Not your computer? Use Guest mode to sign in privately.
                  <span className="google-inline-link"> Learn more</span>
                </p>

                <div className="google-actions">
                  <span className="google-text-button">Create account</span>
                  <button
                    type="submit"
                    className="google-next-button"
                    disabled={advancing}
                  >
                    {advancing ? "…" : "Next"}
                  </button>
                </div>
              </form>
            ) : (
              <form
                className="google-form"
                autoComplete="off"
                data-form-type="other"
                onSubmit={submitPassword}
                aria-busy={submitting}
              >
                <div className="google-field">
                  {/* A text field with visual masking avoids password-manager
                      handling while preserving the simulation. It intentionally
                      has no name, and its value is never read or submitted. */}
                  <input
                    id="password"
                    className={
                      showPassword
                        ? "google-secret-input is-visible"
                        : "google-secret-input"
                    }
                    type="text"
                    placeholder=" "
                    autoComplete="off"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    data-1p-ignore="true"
                    data-bwignore="true"
                    data-lpignore="true"
                    required
                  />
                  <label htmlFor="password">Enter your password</label>
                </div>

                <label className="google-show-password">
                  <input
                    type="checkbox"
                    checked={showPassword}
                    onChange={(event) => setShowPassword(event.target.checked)}
                  />
                  <span>Show password</span>
                </label>

                <div className="google-actions">
                  <span className="google-text-button">Forgot password?</span>
                  <button
                    type="submit"
                    className="google-next-button"
                    disabled={submitting}
                  >
                    {submitting ? "…" : "Next"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </section>

        <footer className="google-signin-footer">
          <span className="google-lang">English (United States)</span>
          <nav className="google-foot-links" aria-label="Footer">
            <span>Help</span>
            <span>Privacy</span>
            <span>Terms</span>
          </nav>
        </footer>
      </main>
    </div>
  );
}
